import React, { useState, useMemo, useEffect } from 'react';
import { View, TouchableWithoutFeedback } from 'react-native';

import {
  map as _map,
  isUndefined as _isUndefined,
  isFunction as _isFunction,
  isEmpty as _isEmpty,
  isNil as _isNil,
} from 'lodash';
import PropTypes from 'prop-types';

import { VFText, Icon, FilterBar, ErrorScreen } from '@vfgroup-oneplatform/foundation/Components';

import usageUsersPropType from '../../Common/PropTypes/usageUsersPropTypes';
import { Images } from '../../Themes';
import SohoFrameworkTestKeys from '../../Utils/SohoFrameworkTestKeys';
import { USAGE_DISPLAY_TYPES, USAGE_CATEGORIES_TYPES } from '../Utils/Enums';

import { UsageLoading } from './Components';
import {
  UnfilteredCategoriesUsage,
  FilteredCategoriesUsage,
  UnfilteredUsersUsage,
  FilteredUsersUsage,
} from './Containers';
import styles from './Usage.Styles';

function getInfoBasedOnDisplayType(displayType, screenConfig) {
  switch (displayType) {
    case USAGE_DISPLAY_TYPES.USERS:
      return {
        title: 'soho_usage_users_title',
        subTitle: 'soho_usage_categories_title',
        description: 'soho_usage_users_description',
        icon: Images.ic_category,
        ...screenConfig?.users,
      };

    case USAGE_DISPLAY_TYPES.CATEGORIES:
    default:
      return {
        title: 'soho_usage_categories_title',
        subTitle: 'soho_usage_users_title',
        description: 'soho_usage_categories_description',
        icon: Images.ic_members,
        ...screenConfig?.categories,
      };
  }
}

function getLabelBasedOnTabId(id) {
  switch (id) {
    case 1:
      return USAGE_CATEGORIES_TYPES.DATA;

    case 2:
      return USAGE_CATEGORIES_TYPES.SMS;

    case 3:
      return USAGE_CATEGORIES_TYPES.CALLS;

    default:
      return USAGE_CATEGORIES_TYPES.SEE_ALL;
  }
}

function getProperUsageInfo(selectedTab, info) {
  const { usageCategories, isUsageCategoriesLoading, usageCategoriesError } = info;

  let usage, isLoading, error;

  switch (selectedTab[0]) {
    case undefined:
      usage = usageCategories?.unfiltered;
      isLoading = isUsageCategoriesLoading?.unfiltered;
      error = usageCategoriesError?.unfiltered;
      break;
    case 1:
      usage = usageCategories?.data;
      isLoading = isUsageCategoriesLoading?.data;
      error = usageCategoriesError?.data;
      break;
    case 2:
      usage = usageCategories?.sms;
      isLoading = isUsageCategoriesLoading?.sms;
      error = usageCategoriesError?.sms;
      break;
    case 3:
      usage = usageCategories?.calls;
      isLoading = isUsageCategoriesLoading?.calls;
      error = usageCategoriesError?.calls;
      break;
    default:
      usage = [];
      isLoading = false;
      error = {};
  }

  return { usage, isLoading, error };
}

function switchDisplayType(
  activeDisplayType,
  setDisplayType,
  setSelectedCategories,
  onDisplayTypeChange,
  user,
) {
  const newDisplayTypes =
    activeDisplayType === USAGE_DISPLAY_TYPES.CATEGORIES
      ? USAGE_DISPLAY_TYPES.USERS
      : USAGE_DISPLAY_TYPES.CATEGORIES;

  setDisplayType(newDisplayTypes);
  setSelectedCategories();
  if (_isFunction(onDisplayTypeChange)) {
    _isNil(user)
      ? onDisplayTypeChange(newDisplayTypes)
      : onDisplayTypeChange(newDisplayTypes, user);
  }
}

function renderLoading({
  isLoading,
  withPercentageView,
  withHeaders,
  withTabs,
  withGraphViewFooter,
}) {
  if (!isLoading) {
    return null;
  }

  return (
    <UsageLoading
      withPercentageView={withPercentageView}
      withHeaders={withHeaders}
      withTabs={withTabs}
      withGraphViewFooter={withGraphViewFooter}
    />
  );
}

function Usage({
  withTray,
  user,
  isLoading,
  usageCategories,
  usageCategoriesError,
  isUsageCategoriesLoading,
  images,
  onUsageCategoriesTabChange,
  usageUsers,
  usageUsersError,
  isUsageUsersLoading,
  onViewGraphsPress,
  onDisplayTypeChange,
  onTryAgainPress,
  screenConfig,
  withSeeAll,
  withFilterBar,
  setActiveSubTabUsageState,
  ...props
}) {
  const [displayType, setDisplayType] = useState(USAGE_DISPLAY_TYPES.CATEGORIES);

  const { icon, description, subTitle, title } = useMemo(
    () => getInfoBasedOnDisplayType(displayType, screenConfig),
    [displayType],
  );
  const [selectedTab, setSelectedTab] = useState([]);

  useEffect(() => {
    if (displayType !== USAGE_DISPLAY_TYPES.USERS) {
      !withSeeAll && handleSelectedCategories(filteredCategories[0]?.id);
    }
  }, [displayType]);

  const setSelectedCategories = id => {
    setSelectedTab(id ? [id] : []);
  };

  const getFilterData = filteringTabs => {
    if (_isEmpty(filteringTabs)) {
      return null;
    }
    return _map(filteringTabs, (tabItem, index) => {
      return { id: index + 1, name: tabItem };
    });
  };

  const renderCategories = _displayType => {
    const {
      error,
      isLoading: isUsageLoading,
      usage,
    } = getProperUsageInfo(selectedTab, {
      usageCategories,
      isUsageCategoriesLoading,
      usageCategoriesError,
    });

    if (isUsageLoading) {
      return renderLoading({
        isLoading: true,
        withPercentageView: _isUndefined(selectedTab[0]),
      });
    }

    if (!_isEmpty(error)) {
      return renderErrorScreen(_displayType, error);
    }

    if (selectedTab.length === 0) {
      return <UnfilteredCategoriesUsage usage={usage} images={images} />;
    } else {
      return <FilteredCategoriesUsage usage={usage} images={images} />;
    }
  };

  const handleSelectedCategories = id => {
    setSelectedCategories(id);
    setActiveSubTabUsageState(id);
    if (_isFunction(onUsageCategoriesTabChange)) {
      const label = getLabelBasedOnTabId(id);

      _isNil(user)
        ? onUsageCategoriesTabChange(label, id)
        : onUsageCategoriesTabChange(label, id, user);
    }
  };
  const categoriesFilteringTabs = [
    'soho_usage_categories_filter_data',
    'soho_usage_categories_filter_sms',
    'soho_usage_categories_filter_calls',
  ];
  const filteredCategories = getFilterData(categoriesFilteringTabs);

  const renderCategoriesUsage = _displayType => {
    if (_displayType === USAGE_DISPLAY_TYPES.USERS) {
      return null;
    }

    return (
      <>
        <FilterBar
          filterData={filteredCategories}
          withSeeAll={withSeeAll}
          selectedCategories={selectedTab}
          setSelectedCategories={handleSelectedCategories}
          containerStyle={styles.filterBarContainerStyle}
          itemTextStyle={styles.itemTextStyle}
        />
        {renderCategories(_displayType)}
      </>
    );
  };

  const renderUsers = _displayType => {
    // withFilterBar value indicates whether we are in PM (False) of BO (True)
    if (isUsageUsersLoading) {
      return renderLoading({
        isLoading: true,
        withPercentageView: true,
        withTabs: withFilterBar,
      });
    }

    if (!_isEmpty(usageUsersError)) {
      return renderErrorScreen(_displayType, usageUsersError);
    }

    if (selectedTab.length === 0 && withFilterBar) {
      return <UnfilteredUsersUsage users={usageUsers} images={images} />;
    } else {
      // withFilterBar value indicates whether
      // we are in PM (False) and here it will just display the only user
      // of BO (True) and here it will take the first user in the usageUsers array
      const userData = withFilterBar
        ? usageUsers?.[selectedTab?.[0] - 1] ?? {}
        : usageUsers[0] ?? {};
      return (
        <FilteredUsersUsage user={userData} images={images} onViewGraphsPress={onViewGraphsPress} />
      );
    }
  };

  const renderUsersUsage = _displayType => {
    if (_displayType === USAGE_DISPLAY_TYPES.CATEGORIES) {
      return null;
    }
    const usersFilteringTabs = _map(usageUsers, u => u.name);
    return (
      <>
        {withFilterBar && (
          <FilterBar
            filterData={getFilterData(usersFilteringTabs)}
            selectedCategories={selectedTab}
            setSelectedCategories={setSelectedCategories}
            containerStyle={styles.filterBarContainerStyle}
            itemTextStyle={styles.itemTextStyle}
            style={styles.containerWithPadding}
          />
        )}
        <View style={styles.containerWithPadding}>{renderUsers(_displayType)}</View>
      </>
    );
  };

  const renderErrorScreen = (_displayType, error) => {
    let errorScreenProps = {};
    if (_displayType === USAGE_DISPLAY_TYPES.CATEGORIES) {
      const [id] = selectedTab;
      const label = getLabelBasedOnTabId(id);
      errorScreenProps = {
        onPress: () => onTryAgainPress?.(_displayType, label, id),
        textButton: 'soho_usage_categories_error_try_again',
        iconStyle: styles.categoriesErrorIconStyle,
      };
    } else {
      errorScreenProps = {
        onPress: () => onTryAgainPress?.(_displayType),
        textButton: 'soho_usage_users_error_try_again',
        iconStyle: styles.usersErrorIconStyle,
      };
    }
    return (
      <ErrorScreen
        backgroundStyle={styles.errorScreenBackgroundStyle}
        text={error?.message}
        {...errorScreenProps}
        {...error}
      />
    );
  };

  const renderContent = _isLoading => {
    if (_isLoading) {
      return null;
    }

    return (
      <>
        <View style={styles.containerWithPadding}>
          <View
            style={styles.titleContainerStyle}
            testID={SohoFrameworkTestKeys.UsageTitleContainer}
          >
            <VFText i18nKey={title} type="primary" style={styles.titleTextStyle} />

            <TouchableWithoutFeedback
              onPress={() =>
                switchDisplayType(
                  displayType,
                  setDisplayType,
                  setSelectedCategories,
                  onDisplayTypeChange,
                  user,
                )
              }
            >
              <View style={styles.subTitleContainerStyle}>
                <VFText i18nKey={subTitle} style={styles.subTitleTextStyle} />
                <Icon name={icon} size={25} />
              </View>
            </TouchableWithoutFeedback>
          </View>

          <VFText i18nKey={description} style={styles.descriptionTextStyle} />

          {renderCategoriesUsage(displayType)}
        </View>

        {renderUsersUsage(displayType)}
      </>
    );
  };

  return (
    <View style={styles.containerStyle(withTray)}>
      <View style={styles.fullLoadingContainer}>
        {renderLoading({
          isLoading,
          withHeaders: true,
          withPercentageView: true,
          withTabs: true,
        })}
      </View>
      {renderContent(isLoading)}
    </View>
  );
}

Usage.defaultProps = {
  withSeeAll: true,
  user: null,
  withFilterBar: true,
};

Usage.propTypes = {
  screenConfig: PropTypes.object,
  withSeeAll: PropTypes.bool,
  withTray: PropTypes.bool,
  isLoading: PropTypes.bool,
  usageCategories: PropTypes.shape({
    unfiltered: PropTypes.array,
    data: PropTypes.array,
    sms: PropTypes.array,
    calls: PropTypes.array,
  }),
  isUsageCategoriesLoading: PropTypes.shape({
    unfiltered: PropTypes.bool,
    data: PropTypes.bool,
    sms: PropTypes.bool,
    calls: PropTypes.bool,
  }),
  usageCategoriesError: PropTypes.shape({
    unfiltered: PropTypes.object,
    data: PropTypes.object,
    sms: PropTypes.object,
    calls: PropTypes.object,
  }),
  images: PropTypes.object,
  onUsageCategoriesTabChange: PropTypes.func,
  usageUsers: usageUsersPropType,
  usageUsersError: PropTypes.object,
  isUsageUsersLoading: PropTypes.bool,
  onViewGraphsPress: PropTypes.func,
  onDisplayTypeChange: PropTypes.func,
  setActiveSubTabUsageState: PropTypes.func,
  onTryAgainPress: PropTypes.func,
  user: PropTypes.object,
  withFilterBar: PropTypes.bool,
};

export {
  getLabelBasedOnTabId,
  getInfoBasedOnDisplayType,
  getProperUsageInfo,
  switchDisplayType,
  renderLoading,
  Usage,
};
export default Usage;
